import { NetworkStatus } from '@apollo/client';
import { ErrorRetry, useAppUser, useAvailableMilestoneTypesQuery } from '@bechtel/client-common';
import { WeldMilestoneType } from '@bechtel/client-common/src/types';
import { Box, Checkbox, Spinner, SpinnerSize, Text } from '@bechtel/gds';
import { MilestoneType } from '@bechtel/types';
import { Trans } from '@lingui/macro';
import { FieldArray, FieldProps } from 'formik';
import { filter, get, orderBy } from 'lodash';
import React, { ChangeEvent, Fragment, useEffect } from 'react';
import { LabelStyled, SubHeader } from '../styled';

export const Milestones: React.FC<FieldProps> = ({ form }) => {
  const { user } = useAppUser();
  const { loading, error, data, refetch, networkStatus } = useAvailableMilestoneTypesQuery(user?.project?.id, true);

  const milestoneList: MilestoneType[] = orderBy(
    filter(get(data, 'projectConfigById.availableMilestoneTypes', []), ({ id }) => {
      return id?.toUpperCase() !== WeldMilestoneType.INSTALL && id?.toUpperCase() !== WeldMilestoneType.FITUP;
    }),
    [(item) => item?.commodityTypes?.[0]?.name?.toLowerCase() || '', (item) => item?.name?.toLowerCase() || ''],
    ['asc', 'asc']
  );

  useEffect(() => {
    form.setFieldValue('milestoneCount', milestoneList.length);
  }, [data]);

  return (
    <Fragment>
      {(loading || networkStatus === NetworkStatus.refetch) && (
        <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} p={{ sm: '500' }} data-testid="loading">
          <Spinner size={SpinnerSize.MEDIUM} />
        </Box>
      )}
      {!loading && error && (
        <Box data-testid="retry">
          <ErrorRetry refetch={refetch}>
            <Trans>There was a problem loading Milestone List. Please click the button below to try again.</Trans>
          </ErrorRetry>
        </Box>
      )}

      {!loading && !error && (
        <Box data-testid="success" flexDirection={{ sm: 'column' }}>
          <FieldArray name="milestone">
            {({ remove, push }) => {
              const onChangeFn = (e: ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value;
                if (form.values.milestone.includes(value)) {
                  const index = form.values.milestone.findIndex((entity: string) => {
                    return entity === value;
                  });

                  remove(index);
                } else {
                  push(value);
                }
              };

              return (
                <Fragment>
                  <SubHeader>
                    <Box>
                      <Box mr={{ sm: '600' }}>
                        <Checkbox
                          type="checkbox"
                          checked={form.values.milestone.length === milestoneList.length}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            e.target.checked
                              ? form.setFieldValue(
                                  'milestone',
                                  milestoneList.map((entity) => {
                                    return entity.id;
                                  })
                                )
                              : form.setFieldValue('milestone', [])
                          }
                        />
                      </Box>
                      <Text color="grey.1000" size="400" weight="600">
                        <Trans>Milestone</Trans>
                      </Text>
                    </Box>
                    {form.values.milestone.length > 0 && (
                      <Text color="grey.1000" size="400">
                        {form.values.milestone.length} <Trans>Selected</Trans>
                      </Text>
                    )}
                  </SubHeader>
                  {milestoneList.map(({ name, id, commodityTypes }, index) => {
                    const displayText = commodityTypes?.[0]?.name ? `${name} (${commodityTypes?.[0]?.name})` : name;
                    return (
                      <LabelStyled key={index} data-testid={`backlog-filter-milestone-types-${id}`}>
                        <Box mr={{ sm: '600' }}>
                          <Checkbox
                            value={id}
                            type="checkbox"
                            onChange={onChangeFn}
                            checked={form.values.milestone.includes(id)}
                          />
                        </Box>
                        <Text color="grey.1000" size="400">
                          {displayText}
                        </Text>
                      </LabelStyled>
                    );
                  })}
                </Fragment>
              );
            }}
          </FieldArray>
        </Box>
      )}
    </Fragment>
  );
};
